import type { ResourceType } from "@istari-frontend/resource/model";

import type {
  DirectoryNode,
  DirectoryNodeId,
  FolderNode,
  JobNode,
  ManagedFileNode,
  ResourceNode,
  RestrictedNode,
  SubsystemNode,
  ZipEntryNode,
} from "./directory-node";

export type DirectoryRowAncestor = {
  // The ancestor's own row id, i.e. its full path from the root, not its node id.
  readonly id: string;
  readonly name: string;
  readonly kind: DirectoryNode["kind"];
  readonly resourceId?: string;
  readonly typeName?: ResourceType;
};

// `hasChildren` is the literal on each arm rather than `boolean` so `"isExpanded" in row` narrows
// to the expandable variant instead of yielding the whole union again. Disjoint arms buy the
// narrowing, not enforcement: a leaf row spread with expansion state still compiles.
type ExpandableNode = FolderNode | SubsystemNode | JobNode | (ResourceNode & { hasChildren: true });
type LeafNode = ManagedFileNode | ZipEntryNode | RestrictedNode | (ResourceNode & { hasChildren: false });

export type DirectoryRow =
  | (ExpandableNode & RowPosition & { readonly isExpanded: boolean; readonly isLoadingChildren: boolean })
  | (LeafNode & RowPosition);

/** The id a row's copy chip copies, with the noun its label names it by. */
export type DirectoryRowCopyId = { id: string; kind: "system id" | "version id" };

export type FolderDirectoryRow = Extract<DirectoryRow, { kind: "folder" }>;
export type SubsystemDirectoryRow = Extract<DirectoryRow, { kind: "subsystem" }>;

export const NO_ANCESTORS: readonly DirectoryRowAncestor[] = Object.freeze([]);

/** A node plus the walked trail, ordered root-first, that picks one of its occurrences. */
export type DirectoryNodeLocation = {
  readonly nodeId: DirectoryNodeId;
  readonly ancestors: readonly DirectoryRowAncestor[];
};

/** A folder under the dependency root: its contents come from packages, not the System's files. */
export function isReservedFolderRow(row: DirectoryRow): boolean {
  return reservedFolderOf(row) !== undefined;
}

/** A folder of the System's that exists only because placements put files in it. */
export function isPlacementOnlyFolderRow(row: DirectoryRow): boolean {
  return row.kind === "folder" && row.role === undefined && row.placementOnly === true;
}

/** What a folder under the dependency root stands for, which picks its icon; undefined for any other row. */
export function reservedFolderOf(row: DirectoryRow): Exclude<FolderNode, { role?: undefined }>["reserved"] | undefined {
  return row.kind === "folder" && row.role !== undefined ? row.reserved : undefined;
}

export function isInaccessibleResourceRow(row: DirectoryRow): boolean {
  return row.kind === "resource" && row.access.status === "resolved" && row.access.value === "inaccessible";
}

// An access check still in flight has granted nothing yet, so the row must not render as if
// it had: presenting it like an accessible one is the fail-open the resolution rules out.
export function isUnresolvedAccessResourceRow(row: DirectoryRow): boolean {
  return row.kind === "resource" && row.access.status === "loading";
}

// Package content can be withheld from a reader who may still see that the file is there.
export function isInaccessibleManagedRow(row: DirectoryRow): boolean {
  return row.kind === "managed" && !row.allowedActions.includes("download");
}

// Detached as in "no resource behind it": the row lists, but every affordance keyed by a
// resource id has nothing to address.
export function isDetachedResourceRow(row: DirectoryRow): boolean {
  return row.kind === "resource" && row.resourceId === undefined;
}

type RowPosition = {
  readonly depth: number;
  // Ancestry belongs to the walked path, not the entity: the same resource can appear
  // under two different expanded subsystems, so a parent is never a node field.
  readonly ancestors: readonly DirectoryRowAncestor[];
};
